import React, { useState } from 'react';
import { X, ShieldCheck, Scale, CheckCircle2, Lock } from 'lucide-react';

interface PrivacyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PrivacyModal: React.FC<PrivacyModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<'privacy' | 'license'>('privacy');

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop">
      <div className="modal-container" style={{ maxWidth: '640px' }}>
        <div className="modal-header">
          <div className="modal-title">
            <ShieldCheck size={18} color="#107C41" />
            <h2>Privacy Notice & Open Source License</h2>
          </div>
          <button onClick={onClose} className="btn-icon">
            <X size={18} />
          </button>
        </div>

        <div className="modal-body">
          <div className="workspace-tabs" style={{ marginBottom: '14px' }}>
            <button
              className={`tab-btn ${activeTab === 'privacy' ? 'active' : ''}`}
              onClick={() => setActiveTab('privacy')}
            >
              <Lock size={14} />
              <span>Privacy Notice (100% Local-First)</span>
            </button>
            <button
              className={`tab-btn ${activeTab === 'license' ? 'active' : ''}`}
              onClick={() => setActiveTab('license')}
            >
              <Scale size={14} />
              <span>MIT License</span>
            </button>
          </div>

          {activeTab === 'privacy' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '12.5px', lineHeight: '1.6', color: '#323130' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  backgroundColor: '#E8F5E9',
                  color: '#2E7D32',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  fontWeight: 600,
                  fontSize: '12px',
                }}
              >
                <CheckCircle2 size={16} />
                <span>Zero Data Collection • 100% Client-Side & Local-First</span>
              </div>

              <div>
                <h4 style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#201F1E' }}>1. Your Data Never Leaves Your Device</h4>
                <p style={{ margin: 0, color: '#605E5C' }}>
                  All table editing, date mathematics, vector SVG rendering, and PNG generation execute strictly inside your local browser memory. No project data, task names, dates, or presentation content are ever sent to remote servers.
                </p>
              </div>

              <div>
                <h4 style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#201F1E' }}>2. No Telemetry or Analytics</h4>
                <p style={{ margin: 0, color: '#605E5C' }}>
                  There are no tracking cookies, analytics trackers, or user activity beacons.
                </p>
              </div>

              <div>
                <h4 style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#201F1E' }}>3. Local Storage Persistence</h4>
                <p style={{ margin: 0, color: '#605E5C' }}>
                  Your task table is stored solely in your browser&apos;s local <code>localStorage</code> on your own computer. You can clear this data at any time by clearing your browser cache or clicking &ldquo;Reset&rdquo; in the app.
                </p>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <p style={{ fontSize: '12px', color: '#605E5C', margin: 0 }}>
                Gantt for M365 is open-source software licensed under the <strong>MIT License</strong>.
              </p>
              <pre
                style={{
                  backgroundColor: '#FAF9F8',
                  border: '1px solid #EDEBE9',
                  borderRadius: '6px',
                  padding: '12px',
                  fontSize: '11px',
                  lineHeight: '1.5',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  maxHeight: '220px',
                  overflowY: 'auto',
                  color: '#323130',
                }}
              >
{`MIT License

Copyright (c) 2024-2026 Marcel Wiedemeier

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.`}
              </pre>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-primary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
